import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Store, ArrowLeft } from 'lucide-react';

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="text-lg font-semibold text-gray-900 mb-3">{title}</h2>
    <div className="text-gray-600 space-y-2 leading-relaxed">{children}</div>
  </div>
);

export default function PrivacyPolicy() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-emerald-600 rounded-lg flex items-center justify-center">
              <Store className="h-4 w-4 text-white" />
            </div>
            <span className="font-bold text-gray-900">KiranaFlow</span>
          </Link>
          <Link to="/home" className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12">
          <h1 className="text-3xl font-extrabold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-sm text-gray-400 mb-10">Effective date: 15 May 2026 · Last updated: 15 May 2026</p>

          <p className="text-gray-600 mb-8 leading-relaxed">
            KiranaFlow ("we", "us", "our") is committed to protecting the personal data and business data
            of our customers. This Privacy Policy explains what data we collect, how we use it, and your rights
            in relation to it. This policy complies with India's Information Technology Act 2000, the IT
            (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules 2011,
            and is aligned with the Digital Personal Data Protection Act 2023 (DPDPA).
          </p>

          <Section title="1. Data We Collect">
            <p><strong className="text-gray-800">Account and contact information</strong></p>
            <ul className="list-disc ml-5 space-y-1 mt-1">
              <li>Owner name, shop name, mobile number, email address</li>
              <li>Business address, city, state, and PIN code</li>
              <li>GSTIN (optional, for GST reporting)</li>
              <li>Login credentials (passwords are managed by Supabase Auth and are never stored in plaintext by us)</li>
            </ul>
            <p className="mt-3"><strong className="text-gray-800">Business and operational data</strong></p>
            <ul className="list-disc ml-5 space-y-1 mt-1">
              <li>Product catalog: names, barcodes, categories, prices, HSN codes, GST rates</li>
              <li>Stock levels and purchase records: supplier names, purchase amounts, dates</li>
              <li>Sales transactions: items sold, quantities, amounts, customer identifiers</li>
              <li>Customer details you add: name, mobile number, address</li>
              <li>Expense records, khata (credit) transactions</li>
            </ul>
            <p className="mt-3"><strong className="text-gray-800">Usage and technical data</strong></p>
            <ul className="list-disc ml-5 space-y-1 mt-1">
              <li>IP address and approximate location (country/region)</li>
              <li>Device type, browser, and operating system</li>
              <li>Pages visited, features used, and session timestamps</li>
              <li>Error logs for debugging and service improvement</li>
            </ul>
            <p className="mt-3"><strong className="text-gray-800">Invoice images (optional AI feature)</strong></p>
            <p>If you use the AI invoice scanning feature, photos or PDFs you upload are sent to our AI provider (Groq) for text extraction. Images are not stored beyond the processing request.</p>
          </Section>

          <Section title="2. How We Use Your Data">
            <p>We use your data to:</p>
            <ul className="list-disc ml-5 space-y-1 mt-1">
              <li>Provide, operate, and maintain the KiranaFlow Service</li>
              <li>Process your transactions, generate bills, and calculate reports</li>
              <li>Generate GST reports (GSTR-1, GSTR-3B) from your sales and purchase data</li>
              <li>Power AI features such as Smart Actions, inventory insights, and invoice parsing</li>
              <li>Send important account communications (subscription confirmations, expiry reminders)</li>
              <li>Diagnose errors and improve Service performance</li>
              <li>Comply with legal obligations under applicable Indian law</li>
            </ul>
            <p className="mt-2">We do not use your business data for advertising, profiling, or any purpose unrelated to providing the Service.</p>
          </Section>

          <Section title="3. Data Sharing">
            <p>We do not sell your personal or business data. We share data only with the following sub-processors who help us operate the Service, each under strict confidentiality obligations:</p>
            <div className="mt-2 overflow-x-auto">
              <table className="text-sm w-full border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-semibold text-gray-700">Provider</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-gray-700">Purpose</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-gray-700">Location</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr><td className="px-4 py-2.5">Supabase</td><td className="px-4 py-2.5">Database and authentication</td><td className="px-4 py-2.5">AWS (Singapore)</td></tr>
                  <tr><td className="px-4 py-2.5">DigitalOcean</td><td className="px-4 py-2.5">Backend server hosting</td><td className="px-4 py-2.5">Bangalore, India</td></tr>
                  <tr><td className="px-4 py-2.5">Vercel</td><td className="px-4 py-2.5">Frontend hosting</td><td className="px-4 py-2.5">Global CDN</td></tr>
                  <tr><td className="px-4 py-2.5">Groq</td><td className="px-4 py-2.5">AI invoice parsing and insights</td><td className="px-4 py-2.5">USA</td></tr>
                </tbody>
              </table>
            </div>
            <p className="mt-3">We may disclose your data to law enforcement or regulatory authorities if required by law or a valid court order.</p>
          </Section>

          <Section title="4. Data Retention">
            <p>We retain your data for as long as your account is active. If you close your account or your subscription lapses and is not renewed within 90 days, we will delete your data within 30 days of your written request, or automatically after 12 months of account inactivity — whichever comes first.</p>
            <p>Certain data (billing records, transaction logs) may be retained for up to 7 years as required by Indian tax and accounting laws.</p>
          </Section>

          <Section title="5. Data Security">
            <p>We implement appropriate technical and organisational safeguards to protect your data, including:</p>
            <ul className="list-disc ml-5 space-y-1 mt-1">
              <li>Encrypted connections (TLS/HTTPS) for all data in transit</li>
              <li>Data at rest encrypted by our database provider (Supabase / AWS)</li>
              <li>Row-level access controls so each store can only access its own data</li>
              <li>JWT-based authentication with short-lived tokens</li>
              <li>Service role credentials stored as server-side secrets, never exposed in the frontend</li>
            </ul>
            <p className="mt-2">No method of transmission or storage is 100% secure. If you believe your account has been compromised, contact us immediately.</p>
          </Section>

          <Section title="6. Your Rights">
            <p>Under the Digital Personal Data Protection Act 2023 (DPDPA) and related regulations, you have the right to:</p>
            <ul className="list-disc ml-5 space-y-1 mt-1">
              <li><strong className="text-gray-800">Access</strong> — request a summary of the personal data we hold about you</li>
              <li><strong className="text-gray-800">Correction</strong> — request correction of inaccurate or incomplete data</li>
              <li><strong className="text-gray-800">Erasure</strong> — request deletion of your data (subject to legal retention obligations)</li>
              <li><strong className="text-gray-800">Data portability</strong> — export your business data at any time via the built-in CSV export features in the app</li>
              <li><strong className="text-gray-800">Grievance redressal</strong> — raise a complaint with our grievance officer (details below)</li>
            </ul>
            <p className="mt-2">To exercise any of these rights, contact us at <a href="mailto:rohankakad712@gmail.com" className="text-emerald-600 underline">rohankakad712@gmail.com</a>. We will respond within 30 days.</p>
          </Section>

          <Section title="7. Cookies and Local Storage">
            <p>KiranaFlow uses browser local storage and IndexedDB to enable offline POS functionality and to cache your product and sales data for faster loading. We do not use advertising cookies or third-party tracking pixels.</p>
          </Section>

          <Section title="8. Children's Privacy">
            <p>The Service is not intended for use by persons under 18 years of age. We do not knowingly collect personal data from minors.</p>
          </Section>

          <Section title="9. Changes to This Policy">
            <p>We may update this Privacy Policy from time to time. We will notify you of material changes by email or in-app notice at least 14 days before they take effect.</p>
          </Section>

          <Section title="10. Grievance Officer">
            <p>As required under Indian law, our designated Grievance Officer for privacy-related complaints is:</p>
            <p className="mt-2">
              <strong>Rohan Kakad</strong><br />
              Email: <a href="mailto:rohankakad712@gmail.com" className="text-emerald-600 underline">rohankakad712@gmail.com</a><br />
              Phone: +91 88888 55212<br />
              Response time: Within 30 days of receipt
            </p>
          </Section>

          <div className="mt-10 pt-6 border-t border-gray-100 flex flex-wrap gap-4 text-sm text-gray-400">
            <Link to="/terms"  className="hover:text-emerald-600 transition-colors">Terms of Service</Link>
            <Link to="/refund" className="hover:text-emerald-600 transition-colors">Refund Policy</Link>
            <Link to="/home"   className="hover:text-emerald-600 transition-colors">Back to Home</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
